import {
  Button,
  Drawer,
  Grid,
  Icons,
  Loader,
  TextController,
  Toast,
} from "@pushpress/shared-components";
import { useForm } from "react-hook-form";
import { DrawerPanel } from "../../../components/common/DrawerPanel";
import { toast } from "react-toastify";
import { defaultToastOptions, extractError } from "../../../utils/misc";
import {
  usePlanCategoryQuery,
  usePostPlanCategoryMutation,
  usePatchPlanCategoryMutation,
} from "../../../apis/subscriptionApi.hooks";
import { useEffect } from "react";

export interface PlanCategoryDrawerProps {
  visible: boolean;
  planCategoryUuid?: string;
  onClose: VoidFunction;
  onSuccess?: VoidFunction;
}

interface CategoryForm {
  name: string;
}

function PlanCategoryDrawer(props: PlanCategoryDrawerProps) {
  const { visible } = props;
  return (
    <Drawer
      visible={visible}
      anchor="right"
      width="400px"
      overflow="visible"
      disableClickAway
      disableEscapeKeyDown
    >
      <PlanCategoryDrawerContent {...props} />
    </Drawer>
  );
}

function PlanCategoryDrawerContent(props: PlanCategoryDrawerProps) {
  const { planCategoryUuid, onClose, onSuccess } = props;

  const { data: planCategory } = usePlanCategoryQuery(planCategoryUuid, {
    enabled: !!planCategoryUuid,
  });

  const createCategoryMutation = usePostPlanCategoryMutation();

  const updateCategoryMutation = usePatchPlanCategoryMutation();

  const { control, reset, handleSubmit } = useForm<CategoryForm>({
    mode: "onTouched",
  });

  const save = async (values: CategoryForm) => {
    try {
      await createCategoryMutation.mutateAsync(values);
      onSuccess?.();
      onClose?.();
      toast.success("Category created successfully", defaultToastOptions);
    } catch (error) {
      toast.error(extractError(error), defaultToastOptions);
    }
  };

  const update = async (values: CategoryForm) => {
    try {
      await updateCategoryMutation.mutateAsync({
        planCategoryUuid,
        name: values.name,
      });
      onSuccess?.();
      onClose?.();
      toast.success("Category updated successfully", defaultToastOptions);
    } catch (error) {
      toast.error(extractError(error), defaultToastOptions);
    }
  };

  const submit = async (values: CategoryForm) => {
    // if planCategoryUuid is present, it means we are editing an existing plan
    if (planCategoryUuid) {
      await update(values);
    } else {
      await save(values);
    }
  };

  // set form values when planCategory is loaded
  useEffect(() => {
    if (planCategory) {
      reset(planCategory);
    }
  }, [planCategory, reset]);

  if (planCategoryUuid && !planCategory) {
    return (
      <Grid container justifyContent="center" className="p-6">
        <Loader />
      </Grid>
    );
  }

  const title = planCategoryUuid ? "Edit category" : "Create category";

  const isLoading =
    createCategoryMutation.isLoading || updateCategoryMutation.isLoading;

  return (
    <DrawerPanel title={title} saving={false} noBodyPadding onClose={onClose}>
      <form onSubmit={handleSubmit(submit)} className="p-4">
        <TextController control={control} name="name" label="Name" required />
        <Toast
          icon={Icons.Info}
          iconColor="success"
          message="Plan categories are used to organize your plans in Core. They will also group your plans in external locations where members are making a purchase. "
        />
        <Button
          text="Save"
          buttonType="primary"
          type="submit"
          fullWidth
          className="mt-4"
          loading={isLoading}
        />
      </form>
    </DrawerPanel>
  );
}

export default PlanCategoryDrawer;
